/** Batched terrain with the same sloped surface used by picking and unit placement. */
import { World, TERRAIN, CELL, GROUND } from '../sim/world.js'
import type { Palette } from '../forge/palette.js'

export const LEVEL_STEP = 1.05
export const WATER_DROP = 0.34
export const WATER_SWELL = 0.012
const GROUND_SUBDIVISION = 2

export interface TerrainGeometryData {
  positions: Float32Array
  normals: Float32Array
  colors: Float32Array
  waves: Float32Array
  indices: Uint32Array
}

export function buildSurfaceHeights(world: World): Float32Array<ArrayBuffer> {
  const out = new Float32Array(world.cells)
  for (let i = 0; i < world.cells; i++) {
    out[i] = (world.flags[i] & CELL.Water) !== 0 ? -WATER_DROP : world.height[i] * LEVEL_STEP
  }
  return out
}

/** Matches the ramp corner priority used by the terrain mesh. */
export function surfaceHeightAt(world: World, surface: Float32Array, x: number, z: number): number {
  const cx = Math.floor(x)
  const cy = Math.floor(z)
  if (!world.inBounds(cx, cy)) return 0
  const cell = cy * world.size + cx
  const height = surface[cell]
  if ((world.flags[cell] & CELL.Ramp) === 0) return height
  if (cx > 0 && surface[cell - 1] < height) return height - (height - surface[cell - 1]) * (1 - (x - cx))
  if (cx + 1 < world.size && surface[cell + 1] < height) return height - (height - surface[cell + 1]) * (x - cx)
  if (cy > 0 && surface[cell - world.size] < height) return height - (height - surface[cell - world.size]) * (1 - (z - cy))
  if (cy + 1 < world.size && surface[cell + world.size] < height) return height - (height - surface[cell + world.size]) * (z - cy)
  return height
}

export function buildWaterDepth(world: World): Uint8Array<ArrayBuffer> {
  const size = world.size
  const out = new Uint8Array(world.cells)
  const isWater = (i: number): boolean => (world.flags[i] & CELL.Water) !== 0

  for (let i = 0; i < world.cells; i++) out[i] = isWater(i) ? 255 : 0

  const relax = (i: number, j: number): void => {
    if (out[j] + 1 < out[i]) out[i] = out[j] + 1
  }
  for (let cy = 0; cy < size; cy++) {
    for (let cx = 0; cx < size; cx++) {
      const i = cy * size + cx
      if (out[i] === 0) continue
      if (cx > 0) relax(i, i - 1)
      if (cy > 0) relax(i, i - size)
    }
  }
  for (let cy = size - 1; cy >= 0; cy--) {
    for (let cx = size - 1; cx >= 0; cx--) {
      const i = cy * size + cx
      if (out[i] === 0) continue
      if (cx < size - 1) relax(i, i + 1)
      if (cy < size - 1) relax(i, i + size)
    }
  }
  return out
}

export function buildTerrainGeometry(world: World, palette: Palette): TerrainGeometryData {
  const positions: number[] = [], normals: number[] = [], colors: number[] = []
  const waves: number[] = [], indices: number[] = []
  const surface = buildSurfaceHeights(world), depth = buildWaterDepth(world), size = world.size
  type Point = [number, number, number]
  type Rgb = [number, number, number]
  const corners = new Float32Array(world.cells * 4)

  for (let i = 0; i < world.cells; i++) {
    const x = i % size, z = Math.floor(i / size), h = surface[i]
    let nw = h, ne = h, se = h, sw = h
    if ((world.flags[i] & CELL.Ramp) !== 0) {
      if (x > 0 && surface[i - 1] < h) nw = sw = surface[i - 1]
      else if (x + 1 < size && surface[i + 1] < h) ne = se = surface[i + 1]
      else if (z > 0 && surface[i - size] < h) nw = ne = surface[i - size]
      else if (z + 1 < size && surface[i + size] < h) sw = se = surface[i + size]
    }
    corners.set([nw, ne, se, sw], i * 4)
  }

  function triangle(a: Point, b: Point, c: Point, color: Rgb, wave: number): void {
    const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]]
    const ac = [c[0] - a[0], c[1] - a[1], c[2] - a[2]]
    const normal = [ab[1] * ac[2] - ab[2] * ac[1], ab[2] * ac[0] - ab[0] * ac[2], ab[0] * ac[1] - ab[1] * ac[0]]
    const length = Math.hypot(...normal)
    if (length < 1e-9) return
    const base = positions.length / 3
    for (const point of [a, b, c]) {
      positions.push(...point)
      normals.push(normal[0] / length, normal[1] / length, normal[2] / length)
      colors.push(...color)
      waves.push(wave)
    }
    indices.push(base, base + 1, base + 2)
  }
  function quad(a: Point, b: Point, c: Point, d: Point, color: Rgb, wave = 0): void {
    triangle(a, c, b, color, wave)
    triangle(a, d, c, color, wave)
  }

  for (let z = 0; z < size; z++) for (let x = 0; x < size; x++) {
    const i = z * size + x, offset = i * 4
    const [nw, ne, se, sw] = corners.subarray(offset, offset + 4)
    const water = (world.flags[i] & CELL.Water) !== 0
    const at = (u: number, v: number): number =>
      nw * (1 - u) * (1 - v) + ne * u * (1 - v) + sw * (1 - u) * v + se * u * v
    for (let dz = 0; dz < GROUND_SUBDIVISION; dz++) for (let dx = 0; dx < GROUND_SUBDIVISION; dx++) {
      const u = dx / GROUND_SUBDIVISION, v = dz / GROUND_SUBDIVISION, step = 1 / GROUND_SUBDIVISION
      const tint = water ? waterColor(palette, depth[i], x + u, z + v) : terrainColor(world, palette, i, x + u, z + v)
      quad([x + u, at(u, v), z + v], [x + u + step, at(u + step, v), z + v],
        [x + u + step, at(u + step, v + step), z + v + step], [x + u, at(u, v + step), z + v + step],
        tint, water ? 1 : 0)
    }

    // All four faces cast shadows. Neighbor corner heights also close ramp edges.
    const points: Point[] = [[x, nw, z], [x + 1, ne, z], [x + 1, se, z + 1], [x, sw, z + 1]]
    const edges = [[0, 1, 0, -1, 3, 2], [1, 2, 1, 0, 0, 3], [2, 3, 0, 1, 1, 0], [3, 0, -1, 0, 2, 1]]
    for (const [a, b, dx, dz, na, nb] of edges) {
      const neighbor = ((z + dz) * size + x + dx) * 4
      const inside = world.inBounds(x + dx, z + dz)
      const topA = points[a], topB = points[b]
      const submerged = !water && inside && (world.flags[neighbor / 4] & CELL.Water) !== 0 ? WATER_SWELL * 3 : 0
      const bottomA = Math.min(topA[1], inside ? corners[neighbor + na] - submerged : -1.4)
      const bottomB = Math.min(topB[1], inside ? corners[neighbor + nb] - submerged : -1.4)
      const drop = Math.max(topA[1] - bottomA, topB[1] - bottomB)
      if (drop < 1e-6) continue
      const bands = Math.ceil(drop / 0.35)
      for (let band = 0; band < bands; band++) {
        const t0 = band / bands, t1 = (band + 1) / bands
        const a0 = topA[1] + (bottomA - topA[1]) * t0, a1 = topA[1] + (bottomA - topA[1]) * t1
        const b0 = topB[1] + (bottomB - topB[1]) * t0, b1 = topB[1] + (bottomB - topB[1]) * t1
        const tint = band === 0 && !water
          ? terrainColor(world, palette, i, x, z)
          : rampColor(palette, 'rock', 0.78 + (band % 2 ? -0.035 : 0.035) + (hash2(x, z) - 0.5) * 0.04)
        quad([topB[0], b0, topB[2]], [topA[0], a0, topA[2]],
          [topA[0], a1, topA[2]], [topB[0], b1, topB[2]], tint)
      }
    }
  }
  return { positions: Float32Array.from(positions), normals: Float32Array.from(normals),
    colors: Float32Array.from(colors), waves: Float32Array.from(waves), indices: Uint32Array.from(indices) }
}

export function rampColor(palette: Palette, ramp: string, t: number): [number, number, number] {
  const steps = palette.terrainRamps[ramp].indices
  const [br, bg, bb] = palette.rgb(steps[steps.length - 1])
  const k = rampScale(palette, ramp, t)
  return [(br * k) / 255, (bg * k) / 255, (bb * k) / 255]
}

/**
 * How bright a point along the ramp is, relative to its top anchor.
 *
 * Derived from the anchors' own luminances rather than from a made-up curve,
 * so a ramp authored with a wider spread still spans that spread.
 */
function rampScale(palette: Palette, ramp: string, t: number): number {
  const steps = palette.terrainRamps[ramp].indices
  const lum = (i: number): number => {
    const [r, g, b] = palette.rgb(steps[i])
    return r + g + b
  }
  const top = lum(steps.length - 1) || 1
  const scaled = Math.max(0, Math.min(1, t)) * (steps.length - 1)
  const i = Math.min(steps.length - 2, Math.floor(scaled))
  const f = scaled - i
  return (lum(i) + (lum(i + 1) - lum(i)) * f) / top
}

function terrainColor(world: World, palette: Palette, cell: number, x: number, z: number): [number, number, number] {
  const kind = world.terrain[cell]
  if (world.ground[cell] !== GROUND.Natural) return [0.36, 0.40, 0.38]
  const ramp = kind === TERRAIN.Regolith ? 'regolith' : kind === TERRAIN.Verdant ? 'verdant'
    : world.height[cell] === 2 ? 'basalt' : 'dust'
  const tint = rampColor(palette, ramp, 0.63)
  const broad = smoothNoise(x * 0.55, z * 0.55)
  const grain = hash2(Math.round(x * GROUND_SUBDIVISION), Math.round(z * GROUND_SUBDIVISION))
  const shade = 1 + (broad - 0.5) * 0.18 + (grain - 0.5) * 0.035
  return [tint[0] * shade, tint[1] * shade, tint[2] * shade]
}

function waterColor(palette: Palette, depth: number, x: number, z: number): [number, number, number] {
  return rampColor(palette, 'ice', 0.86 - Math.min(1, depth / 5) * 0.26 + (smoothNoise(x, z) - 0.5) * 0.1)
}

export function smoothNoise(cx: number, cy: number): number {
  return lattice(cx / 3.5, cy / 3.5) * 0.65 + lattice(cx / 11, cy / 11) * 0.35
}

function lattice(x: number, y: number): number {
  const x0 = Math.floor(x)
  const y0 = Math.floor(y)
  const fx = smoothstep(x - x0)
  const fy = smoothstep(y - y0)
  const v00 = hash2(x0, y0)
  const v10 = hash2(x0 + 1, y0)
  const v01 = hash2(x0, y0 + 1)
  const v11 = hash2(x0 + 1, y0 + 1)
  return (v00 + (v10 - v00) * fx) * (1 - fy) + (v01 + (v11 - v01) * fx) * fy
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t)
}

export function hash2(x: number, y: number): number {
  let h = Math.imul(x | 0, 0x27d4eb2f) ^ Math.imul(y | 0, 0x165667b1)
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b)
  h ^= h >>> 13
  return (h >>> 0) / 4294967296
}
